import React, { useState, useEffect } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { voiceActorService, getMyFollowerCount } from '../../services/voiceActorService';
import { RealtimeSubscriptionService } from '../../services/realtimeSubscriptionService';
import notificationService from '../../services/notificationService';
import Icon from '../../components/AppIcon';
import AssignmentCard from './components/AssignmentCard';
import PerformanceMetrics from './components/PerformanceMetrics';
import AvailabilityCalendar from './components/AvailabilityCalendar';
import RecordingHistory from './components/RecordingHistory';
import QuickActions from './components/QuickActions';
import RealtimeNotifications from './components/RealtimeNotifications';
import NotificationPreferences from './components/NotificationPreferences';
import NotificationCenter from '../../components/navigation/NotificationCenter';


const VoiceActorPortalDashboard = () => {
  const { user, userProfile } = useAuth();
  const [voiceActorProfile, setVoiceActorProfile] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [recordingSessions, setRecordingSessions] = useState([]);
  const [performanceStats, setPerformanceStats] = useState(null);
  const [followerCount, setFollowerCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeService] = useState(() => new RealtimeSubscriptionService());
  const [notifications, setNotifications] = useState([]);
  const [inAppNotifications, setInAppNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    // Check URL for tab parameter
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams?.get('tab');
    if (tab === 'notifications') {
      setActiveTab('notifications');
    }
  }, []);

  useEffect(() => {
    const fetchVoiceActorData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch voice actor profile
        const actors = await voiceActorService?.getAllVoiceActors();
        const actorProfile = actors?.find(actor => actor?.userId === user?.id);
        
        if (!actorProfile) {
          setError('Voice actor profile not found');
          setLoading(false);
          return;
        }

        setVoiceActorProfile(actorProfile);

        // Fetch assignments for this voice actor
        const assignmentsData = await voiceActorService?.getAssignments({
          voiceActorId: actorProfile?.id
        });
        setAssignments(assignmentsData);

        // Fetch upcoming deadlines
        const deadlines = await voiceActorService?.getUpcomingDeadlines(7);
        const myDeadlines = deadlines?.filter(d => d?.voiceActorId === actorProfile?.id);
        setUpcomingDeadlines(myDeadlines);

        // Fetch recording sessions
        const sessions = await voiceActorService?.getRecordingSessions({
          voiceActorId: actorProfile?.id
        });
        setRecordingSessions(sessions);

        // Fetch follower count
        const followers = await getMyFollowerCount();
        setFollowerCount(followers);

        // Fetch notifications
        const notificationsData = await notificationService?.getNotifications({ limit: 10 });
        setNotifications(notificationsData);

        // Calculate performance statistics
        const stats = calculatePerformanceStats(assignmentsData, sessions);
        setPerformanceStats(stats);

      } catch (err) {
        console.error('Error fetching voice actor data:', err);
        setError(err?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchVoiceActorData();
  }, [user?.id]);

  // Real-time subscriptions for notifications
  useEffect(() => {
    if (!voiceActorProfile?.id) return;

    const unsubscribe = notificationService?.subscribeToNotifications((newNotification) => {
      console.log('New notification received:', newNotification);
      
      // Add to notifications list
      setNotifications(prev => [newNotification, ...prev]);
      
      // Show toast notification
      if (newNotification?.type === 'new_follower') {
        addNotification(newNotification?.message, 'assignment');
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [voiceActorProfile?.id]);

  // Real-time subscriptions for assignment updates
  useEffect(() => {
    if (!voiceActorProfile?.id) return;

    const handleAssignmentUpdate = async (payload) => {
      console.log('Assignment update received:', payload);
      
      try {
        // Refetch assignments to get updated data with relationships
        const updatedAssignments = await voiceActorService?.getAssignments({
          voiceActorId: voiceActorProfile?.id
        });
        setAssignments(updatedAssignments);

        // Update deadlines
        const deadlines = await voiceActorService?.getUpcomingDeadlines(7);
        const myDeadlines = deadlines?.filter(d => d?.voiceActorId === voiceActorProfile?.id);
        setUpcomingDeadlines(myDeadlines);

        // Recalculate performance stats
        const sessions = await voiceActorService?.getRecordingSessions({
          voiceActorId: voiceActorProfile?.id
        });
        const stats = calculatePerformanceStats(updatedAssignments, sessions);
        setPerformanceStats(stats);

        // Show notification
        const notificationMessage = getAssignmentNotificationMessage(payload);
        if (notificationMessage) {
          addNotification(notificationMessage, 'assignment');
        }
      } catch (err) {
        console.error('Error handling assignment update:', err);
      }
    };

    const unsubscribeAssignments = realtimeService?.subscribeToVoiceActorAssignments(
      voiceActorProfile?.id,
      handleAssignmentUpdate
    );

    return () => {
      if (unsubscribeAssignments) unsubscribeAssignments();
    };
  }, [voiceActorProfile?.id, realtimeService]);

  // Real-time subscriptions for recording session updates
  useEffect(() => {
    if (!voiceActorProfile?.id) return;

    const handleSessionUpdate = async (payload) => {
      console.log('Recording session update received:', payload);
      
      try {
        // Refetch recording sessions
        const sessions = await voiceActorService?.getRecordingSessions({
          voiceActorId: voiceActorProfile?.id
        });
        setRecordingSessions(sessions);

        // Recalculate performance stats
        const stats = calculatePerformanceStats(assignments, sessions);
        setPerformanceStats(stats);

        // Show notification
        const notificationMessage = getSessionNotificationMessage(payload);
        if (notificationMessage) {
          addNotification(notificationMessage, 'session');
        }
      } catch (err) {
        console.error('Error handling session update:', err);
      }
    };

    const unsubscribeSessions = realtimeService?.subscribeToVoiceActorRecordingSessions(
      voiceActorProfile?.id,
      handleSessionUpdate
    );

    return () => {
      if (unsubscribeSessions) unsubscribeSessions();
    };
  }, [voiceActorProfile?.id, assignments, realtimeService]);

  // Real-time subscriptions for performance feedback (pronunciation analytics)
  useEffect(() => {
    if (!user?.id) return;

    const handlePerformanceFeedback = (payload) => {
      console.log('Performance feedback received:', payload);
      
      // Show notification for performance updates
      const notificationMessage = getPerformanceNotificationMessage(payload);
      if (notificationMessage) {
        addNotification(notificationMessage, 'performance');
      }
    };

    const unsubscribePerformance = realtimeService?.subscribeToPronunciationAnalytics(
      user?.id,
      handlePerformanceFeedback
    );

    return () => {
      if (unsubscribePerformance) unsubscribePerformance();
    };
  }, [user?.id, realtimeService]);

  const getAssignmentNotificationMessage = (payload) => {
    const { eventType, new: newData, old: oldData } = payload;
    
    if (eventType === 'INSERT') {
      return 'New assignment received!';
    } else if (eventType === 'UPDATE') {
      if (oldData?.assignment_status !== newData?.assignment_status) {
        return `Assignment status updated to ${newData?.assignment_status}`;
      }
      if (oldData?.due_date !== newData?.due_date) {
        return 'Assignment deadline has been updated';
      }
      return 'Assignment details updated';
    } else if (eventType === 'DELETE') {
      return 'An assignment has been removed';
    }
    return null;
  };

  const getSessionNotificationMessage = (payload) => {
    const { eventType, new: newData, old: oldData } = payload;
    
    if (eventType === 'INSERT') {
      return 'New recording session scheduled!';
    } else if (eventType === 'UPDATE') {
      if (oldData?.status !== newData?.status) {
        return `Recording session status: ${newData?.status}`;
      }
      return 'Recording session updated';
    } else if (eventType === 'DELETE') {
      return 'A recording session has been cancelled';
    }
    return null;
  };

  const getPerformanceNotificationMessage = (payload) => {
    const { eventType, new: newData } = payload;
    
    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      const avgScore = newData?.average_score;
      if (avgScore >= 90) {
        return `Excellent performance! Average score: ${avgScore}%`;
      } else if (avgScore >= 75) {
        return `Good progress! Average score: ${avgScore}%`;
      }
      return 'Performance feedback updated';
    }
    return null;
  };

  const addNotification = (message, type) => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    };
    
    setNotifications(prev => [notification, ...prev]?.slice(0, 5)); // Keep last 5 notifications
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev?.filter(n => n?.id !== notification?.id));
    }, 5000);
  };

  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
  };

  const handleNotificationDismiss = async (notificationId) => {
    try {
      await notificationService?.markAsRead(notificationId);
      setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  };

  const calculatePerformanceStats = (assignmentsData, sessions) => {
    const totalAssignments = assignmentsData?.length;
    const completedAssignments = assignmentsData?.filter(a => a?.status === 'completed')?.length;
    const inProgressAssignments = assignmentsData?.filter(a => a?.status === 'in_progress')?.length;
    const pendingAssignments = assignmentsData?.filter(a => a?.status === 'pending')?.length;
    
    const completionRate = totalAssignments > 0 
      ? Math.round((completedAssignments / totalAssignments) * 100) 
      : 0;

    const totalLines = assignmentsData?.reduce((sum, a) => sum + (a?.lineGroup?.lineCount || 0), 0);
    const completedLines = assignmentsData?.filter(a => a?.status === 'completed')?.reduce((sum, a) => sum + (a?.lineGroup?.lineCount || 0), 0);

    const completedSessions = sessions?.filter(s => s?.status === 'completed')?.length;
    const upcomingSessions = sessions?.filter(s => s?.status === 'confirmed' || s?.status === 'pending')?.length;

    return {
      totalAssignments,
      completedAssignments,
      inProgressAssignments,
      pendingAssignments,
      completionRate,
      totalLines,
      completedLines,
      completedSessions,
      upcomingSessions
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Dashboard</h2>
            <p className="text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation userRole={userProfile?.role}>
        <NotificationCenter 
          notifications={inAppNotifications} 
          onNotificationDismiss={handleNotificationDismiss}
          userRole="voice_actor"
        />
      </RoleBasedNavigation>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Voice Actor Portal
          </h1>
          <p className="text-muted-foreground">
            Welcome back, {voiceActorProfile?.stageName || userProfile?.fullName}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-border">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                activeTab === 'dashboard' ?'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                activeTab === 'notifications'
                  ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Notification Settings
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Icon name="Loader" className="animate-spin text-accent" size={48} />
          </div>
        ) : error ? (
          <div className="bg-error/10 border border-error rounded-lg p-6 flex items-start gap-4">
            <Icon name="AlertCircle" size={24} className="text-error flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-error mb-2">Error Loading Dashboard</h3>
              <p className="text-error/80">{error}</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  <PerformanceMetrics stats={performanceStats} followerCount={followerCount} />
                  <AssignmentCard assignment={assignments} upcomingDeadlines={upcomingDeadlines} />
                  <RecordingHistory assignments={assignments} sessions={recordingSessions} />
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  <QuickActions />
                  <AvailabilityCalendar voiceActorId={voiceActorProfile?.id} sessions={recordingSessions} />
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <NotificationPreferences />
            )}
          </>
        )}
      </div>

      <RealtimeNotifications notifications={notifications} onDismiss={dismissNotification} />
    </div>
  );
};

export default VoiceActorPortalDashboard;